import { useCallback, useEffect, useRef, useState } from 'react';
import API from 'services/API';
import { FileStatus } from 'UI/constants/status';
import { getErrorMessage } from 'UI/utils';

const buildUploadingFile = file => ({
  file_name: file.name,
  original_name: file.name,
  size: file.size,
  ext: file.name.split('.').pop(),
  isLoading: true,
  type: FileStatus.New
});

const getFileSyncKey = file => {
  if (!file) return null;

  return file.id || file.url || file.file_name || file.original_name || null;
};

export const useOfferLetterUpload = ({
  disabled = false,
  file = null,
  onTempFileUploaded = async () => {},
  uploadEndpoint
}) => {
  const inputRef = useRef(null);
  const persistedFileRef = useRef(file);
  const [currentFile, setCurrentFile] = useState(file);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const clearLocalError = useCallback(() => setError(null), []);

  useEffect(() => {
    if (getFileSyncKey(persistedFileRef.current) === getFileSyncKey(file)) return;

    persistedFileRef.current = file;
    clearLocalError();
    setCurrentFile(file);
  }, [clearLocalError, file]);

  const openFilePicker = useCallback(() => {
    if (disabled || isUploading || isSaving) return;

    if (inputRef.current) {
      inputRef.current.click();
    }
  }, [disabled, isSaving, isUploading]);

  const handleInputChange = useCallback(
    async event => {
      const input = event.target;
      const selectedFile = input.files?.[0];

      if (!selectedFile || disabled || isUploading || isSaving) {
        input.value = '';
        return;
      }

      const previousPersistedFile = persistedFileRef.current;
      const data = new FormData();
      data.append('file', selectedFile);

      clearLocalError();
      setIsUploading(true);
      setCurrentFile(buildUploadingFile(selectedFile));

      try {
        const uploadResponse = await API.post(uploadEndpoint, data);
        const tempFile = {
          ...uploadResponse?.data,
          file_name:
            uploadResponse?.data?.file_name ||
            uploadResponse?.data?.original_name ||
            selectedFile.name,
          original_name: uploadResponse?.data?.original_name || selectedFile.name,
          size: uploadResponse?.data?.size || selectedFile.size,
          type: FileStatus.New
        };

        setCurrentFile(tempFile);
        setIsSaving(true);

        try {
          await onTempFileUploaded(tempFile);
        } catch (saveError) {
          setCurrentFile(previousPersistedFile);
          setError(getErrorMessage(saveError));
        } finally {
          setIsSaving(false);
        }
      } catch (uploadError) {
        setCurrentFile(previousPersistedFile);
        setError(getErrorMessage(uploadError));
      } finally {
        setIsUploading(false);
        input.value = '';
      }
    },
    [clearLocalError, disabled, isSaving, isUploading, onTempFileUploaded, uploadEndpoint]
  );

  return {
    currentFile,
    inputRef,
    isUploading,
    isSaving,
    error,
    openFilePicker,
    handleInputChange,
    clearLocalError
  };
};
